import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Mail, 
  Phone, 
  Building, 
  Globe, 
  Linkedin, 
  Instagram, 
  MessageCircle, 
  Youtube,
  Download,
  User,
  Share2,
  Copy,
  Facebook,
  Twitter,
  Github
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Discord icon component
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function PublicProfile() {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // 301-style redirect: if URL has uppercase, redirect to lowercase
    if (username !== username.toLowerCase()) {
      navigate(`/user/${username.toLowerCase()}`, { replace: true });
      return;
    }
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/user/${username}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        const errorData = await response.json();
        setError(true);
        setErrorMessage(errorData.detail || t('errors.profileNotFound'));
      }
    } catch (err) {
      setError(true);
      setErrorMessage(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVCard = () => {
    window.location.href = `${API}/vcard/${username}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('profile.linkCopied'));
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareWhatsApp = () => {
    const text = `Check out my digital business card: ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-slate-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            {errorMessage || t('errors.profileNotFound')}
          </h1>
          <p className="text-slate-600">
            {errorMessage === t('errors.profileInactive') 
              ? t('errors.profileInactive')
              : "The profile you're looking for doesn't exist or is not public."
            }
          </p>
        </div>
      </div>
    );
  }

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.profile_id;

  const contactLinks = [
    { icon: Mail, value: profile.email, href: `mailto:${profile.email}`, label: t('profile.email') },
    { icon: Phone, value: profile.phone, href: `tel:${profile.phone}`, label: t('profile.phone') },
    { icon: Building, value: profile.office_phone, href: `tel:${profile.office_phone}`, label: t('profile.officePhone') },
    { icon: Globe, value: profile.website, href: profile.website, label: t('profile.website') },
  ].filter(item => item.value);

  const socialLinks = [
    { icon: Linkedin, value: profile.linkedin, href: profile.linkedin, label: 'LinkedIn', color: 'bg-blue-600' },
    { icon: Instagram, value: profile.instagram, href: profile.instagram, label: 'Instagram', color: 'bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500' },
    { icon: MessageCircle, value: profile.whatsapp, href: `https://wa.me/${profile.whatsapp?.replace(/\D/g, '')}`, label: 'WhatsApp', color: 'bg-green-500' },
    { icon: Facebook, value: profile.facebook, href: profile.facebook, label: 'Facebook', color: 'bg-[#0A2540]/50' },
    { icon: Twitter, value: profile.twitter, href: profile.twitter, label: 'X (Twitter)', color: 'bg-slate-800' },
    { icon: DiscordIcon, value: profile.discord, href: profile.discord, label: 'Discord', color: 'bg-indigo-500' },
    { icon: TikTokIcon, value: profile.tiktok, href: profile.tiktok, label: 'TikTok', color: 'bg-slate-900' },
    { icon: Github, value: profile.github, href: profile.github, label: 'GitHub', color: 'bg-slate-700' },
    { icon: Youtube, value: profile.youtube_channel, href: profile.youtube_channel, label: t('profile.youtubeChannel'), color: 'bg-red-600' },
  ].filter(item => item.value);

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4" data-testid="public-profile">
      <div className="max-w-md mx-auto">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden"
        >
          {/* Header with banner */}
          <div className="relative h-32 overflow-hidden">
            {profile.banner?.type === 'image' ? (
              <img
                src={profile.banner.value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profile.banner.value}` : profile.banner.value}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full"
                style={{ backgroundColor: profile.banner?.value || '#1e293b' }}
              />
            )}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-4 right-4 w-20 h-20 bg-white/30 rounded-full blur-2xl" />
              <div className="absolute bottom-4 left-4 w-16 h-16 bg-white/20 rounded-full blur-xl" />
            </div>
          </div>

          {/* Avatar */}
          <div className="relative px-6 -mt-16">
            <div className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-lg overflow-hidden bg-slate-200">
              {profile.photo ? (
                <img 
                  src={profile.photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo}` : profile.photo} 
                  alt={fullName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-100">
                  <span className="text-4xl font-bold text-slate-400">
                    {fullName[0]?.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Name & Title */}
          <div className="text-center px-6 pt-4 pb-6">
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {fullName}
            </h1>
            {profile.job_title && (
              <p className="text-slate-600 mt-1">{profile.job_title}</p>
            )}
            {profile.company && (
              <p className="text-slate-500 text-sm mt-1">{profile.company}</p>
            )}
            {profile.bio && (
              <p className="text-slate-600 text-sm mt-4 leading-relaxed">
                {profile.bio}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6 flex gap-3">
            <Button 
              onClick={handleDownloadVCard}
              className="flex-1 btn-primary py-5"
              data-testid="download-vcard-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('profile.downloadContact')}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full px-4 py-5" data-testid="share-btn">
                  <Share2 className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleCopyLink} data-testid="copy-link">
                  <Copy className="w-4 h-4 mr-2" />
                  {t('profile.copyLink')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleShareWhatsApp} data-testid="share-whatsapp">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Contact Links */}
          {contactLinks.length > 0 && (
            <div className="px-6 pb-6">
              <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                Contact
              </h2>
              <div className="space-y-3">
                {contactLinks.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <motion.a
                      key={idx}
                      href={link.href}
                      target={link.href?.startsWith('http') ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      data-testid={`contact-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <Icon className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">{link.label}</p>
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {link.value}
                        </p>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="px-6 pb-8">
              <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                Social
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {socialLinks.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <motion.a
                      key={idx}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      data-testid={`social-${link.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      <div className={`w-10 h-10 ${link.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {link.label}
                      </span>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          )}

          {/* YouTube Video Embed */}
          {profile.youtube_video && profile.youtube_video.includes('youtube.com') && (
            <div className="px-6 pb-8">
              <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                Video
              </h2>
              <div className="aspect-video rounded-xl overflow-hidden bg-slate-100">
                <iframe
                  src={profile.youtube_video.replace('watch?v=', 'embed/')}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 text-center">
            <p className="text-xs text-slate-500">
              Powered by <span className="font-medium text-slate-700">IKATone</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
